/**
 * UsernameCheckHint.tsx — one-line status under a username field
 * (checking / available / taken / invalid). Pairs with
 * useUsernameAvailability() from "@/lib/username".
 */
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

import type { UsernameCheckState } from "@/lib/username";

interface UsernameCheckHintProps {
  state: UsernameCheckState;
  /** Shown when the field is empty (e.g. "Leave blank to use the email"). */
  idleHint?: string;
  /** True when the checked value is the email default, not a typed username. */
  usingEmailDefault?: boolean;
}

export function UsernameCheckHint({ state, idleHint, usingEmailDefault }: UsernameCheckHintProps) {
  switch (state.status) {
    case "checking":
      return (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking availability…
        </p>
      );
    case "available":
      return (
        <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400" aria-live="polite">
          <CheckCircle2 className="h-3 w-3" />
          {usingEmailDefault
            ? `Username will be ${state.username} (the email). It's available.`
            : `${state.username} is available.`}
        </p>
      );
    case "taken":
      return (
        <p className="flex items-start gap-1.5 text-xs text-destructive" aria-live="polite">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            {state.message}
            {usingEmailDefault && " Enter a username above to reuse this email."}
          </span>
        </p>
      );
    case "invalid":
      return (
        <p className="flex items-start gap-1.5 text-xs text-destructive" aria-live="polite">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{state.message}</span>
        </p>
      );
    case "error":
      return (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          Couldn't check availability right now — it will be checked again when you save.
        </p>
      );
    default:
      return idleHint ? <p className="text-xs text-muted-foreground">{idleHint}</p> : null;
  }
}
